"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import WinHeader from "./components/WinHeader";
import HeroSlider from "./components/HeroSlider";
import HowItWorks from "./components/HowItWorks";
import SaveSection from "./components/SaveSection";
import StickyCTA from "./components/StickyCTA";
import Modal from "@/app/components/Modal/modal";
import SubscriptionPlansModal from "@/app/(custom-header)/subscriber-competitions/components/subscriptionPlansModal";
import { useAppDispatch, useAppSelector } from "@/app/redux/hooks";
import { publishModalData, publishModalProps, publishModalVisibility } from "@/app/redux/features/modal/modalSlice";
import { getSubscriptionOfferData } from "./win-service";
import { getSubscriptionData } from "@/app/(general-header)/botb-pass/subscription-service";
import "./win.css";

const WinPageContent = () => {
  const dispatch = useAppDispatch();
  const isModalOpen = useAppSelector(state => state.modal.isOpen);
  const modalData = useAppSelector(state => state.modal.data);
  const modalOverlay = useAppSelector(state => state.modal.withOverlay);
  const modalClassNames = useAppSelector(state => state.modal.modalClassNames);
  const modalStyles = useAppSelector(state => state.modal.modalStyles);

  const [offerData, setOfferData] = useState<any>(null);
  const [legalText, setLegalText] = useState<string>("");
  const [isStickyCTAVisible, setIsStickyCTAVisible] = useState(false);
  const mainOfferRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getSubscriptionOfferData()
      .then(res => res.json())
      .then(res => {
        setOfferData(res?.offer || null);
      })
      .catch(err => console.error("Error fetching offer data:", err));
  }, []);

  useEffect(() => {
    getSubscriptionData()
      .then(res => res.json())
      .then(res => {
        setLegalText(res?.header?.legalText || "");
      })
      .catch(err => console.error("Error fetching subscription data:", err));
  }, []);

  useEffect(() => {
    if (!mainOfferRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsStickyCTAVisible(!entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(mainOfferRef.current);
    return () => observer.disconnect();
  }, []);

  const handleGetBotbPass = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    const utm = searchParams.get("utm_medium") || "win";
    dispatch(
      publishModalData({
        modalData: <SubscriptionPlansModal place={utm} />,
      })
    );
    dispatch(
      publishModalProps({
        withOverlay: false,
      })
    );
    dispatch(publishModalVisibility({ isOpen: true }));
  }, [dispatch]);

  const handleBuyTickets = useCallback(() => {
    window.location.href = "/";
  }, []);

  return (
    <div className="font-jost w-full bg-white">
      <WinHeader />

      <main>
        <HeroSlider
          ref={mainOfferRef}
          offerData={offerData}
          onGetBotbPass={handleGetBotbPass}
          onBuyTickets={handleBuyTickets}
        />

        <HowItWorks onGetBotbPass={handleGetBotbPass} onBuyTickets={handleBuyTickets} />

        <SaveSection />

        <div className="w-full py-6 px-4 lg:pt-8 lg:pb-12 lg:px-8">
          <div className="max-w-[962px] mx-auto">
            <div className="font-jost text-[12px] font-normal leading-3.5 text-gray-1 text-center">
              {legalText && <div className="mb-3.5">{legalText}</div>}
              <div>
                <a href="/terms" className="underline cursor-pointer">
                  Terms and Conditions apply
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>

      <StickyCTA visible={isStickyCTAVisible} onGetBotbPass={handleGetBotbPass} onBuyTickets={handleBuyTickets} />

      <Modal
        withOverlay={modalOverlay}
        closeModal={event => {
          event.stopPropagation();
          dispatch(publishModalVisibility({ isOpen: false }));
        }}
        isOpen={isModalOpen}
        body={modalData}
        title=""
        modalClassNames={modalClassNames}
        customStyles={modalStyles}
      />
    </div>
  );
};

export default WinPageContent;
